import { useQuery } from "@tanstack/react-query";
import { queryClient, trpc } from "@/trpc/client";
import { awaitSignupProvisioning } from "@/lib/signupProvisioning";
import { pickEntryWorkspace } from "@/lib/lastWorkspace";

// Prefetched in workspace-route beforeLoad; keep fresh long enough that the
// layout's useQuery reuses the cache instead of refetching on every mount.
const WORKSPACES_STALE_TIME_MS = 60_000;

export const workspacesQueryOptions = {
  ...trpc.authenticatedRouterFunction.getWorkspaces.queryOptions({}),
  // Only a non-empty list is cacheable. An empty list for a signed-in user is
  // ambiguous: it may be a transient mid-signup read taken before createUser
  // finishes provisioning the first workspace, so pinning that answer for 60s
  // is what kept a freshly-registered user bouncing back to `/` across reloads
  // and "Dashboard" clicks even after the workspace existed server-side.
  // Re-reading an empty list every time is cheap and self-correcting; a
  // genuinely workspace-less user simply reads empty again and is handled
  // downstream (dashboard index redirects to `/`).
  staleTime: (query: { state: { data?: unknown[] } }) =>
    query.state.data?.length ? WORKSPACES_STALE_TIME_MS : 0,
};

export async function getUserWorkspaces() {
  // Never read while signup is still creating the row + first workspace: the
  // empty list it would return is indistinguishable from "not a customer".
  await awaitSignupProvisioning();
  // fetchQuery (not ensureQueryData) so a stale entry left by a previous auth
  // session is refetched instead of returned. ensureQueryData returns cached
  // data unconditionally when present; staleTime only governs fetchQuery.
  return queryClient.fetchQuery(workspacesQueryOptions);
}

/**
 * Which workspace a user entering through `/dashboard` — or bounced off an
 * inaccessible one — should land in (#1014). Was `getFirstWorkspace`, and for a
 * single-workspace user it still answers exactly that.
 *
 * The browser's remembered workspace wins when the caller still belongs to it;
 * otherwise the first in creation order (`getWorkspacesFromDB` orders the list,
 * so "first" is stable rather than whatever Postgres returned).
 *
 * The membership re-check is the whole safety story: the remembered id is an
 * opaque string from localStorage, which a different account on this browser
 * may have written. Matching it against the CALLER's list is what makes an
 * unusable value a no-op instead of a redirect into someone else's workspace —
 * and `guardWorkspaceAccess` would refuse it a second time anyway.
 */
export async function getEntryWorkspace() {
  await awaitSignupProvisioning();
  // Reuse workspacesQueryOptions so this call shares the dashboard layout's
  // cache entry (no separate `{ limit: 1 }` key) and respects its staleTime.
  // Cross-auth leakage is handled by clearQueryCacheOnAuthChange in auth.ts.
  const workspaces = await queryClient.fetchQuery(workspacesQueryOptions);

  return pickEntryWorkspace(workspaces);
}

export function isAccessibleWorkspace(
  workspaces: Awaited<ReturnType<typeof getUserWorkspaces>>,
  workspaceId: string,
) {
  return workspaces.some((workspace) => workspace.workspaceId === workspaceId);
}

/** The saved full hostname from the existing workspace projection. */
export function useGoodsenderDomain(workspaceId: string) {
  const query = useQuery(workspacesQueryOptions);
  return Array.isArray(query.data)
    ? query.data.find(workspace => workspace.workspaceId === workspaceId)?.goodsenderDomain
    : undefined;
}
